import { useState, type ReactElement } from "react";
import { Trash2 } from "lucide-react";
import type { GoalsParams, GoalRow } from "@playbookai/widget-schemas";
import { Button } from "../../atoms/Button/Button.js";
import { Input } from "../../atoms/Input/Input.js";
import {
  DataTable,
  DisabledReason,
  EmptyState,
  type DataTableColumn,
} from "../../molecules/index.js";
import type { WidgetBodyProps } from "../types.js";

/**
 * Goals — the VSM charter's baseline → target → % change table (VSM-WS10). A
 * small editable grid: each row names one metric with a baseline, a target, and
 * a display unit; the **% change is derived here** (never stored). Data-blind
 * and inline-editable — renders `data`, emits every add/remove/edit through
 * `onChange`; `readOnly` hides the edit affordances. Numeric cells commit on
 * blur (so intermediate strings like "1." survive typing); text cells are
 * controlled. The grid is the design-system DataTable with ghost Inputs as its
 * cell editors.
 */

function makeId(prefix: string): string {
  const rnd =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `${prefix}-${rnd}`;
}

/** Matches GoalsParamsSchema's `z.array(GoalRow).max(50)`. */
const MAX_GOAL_ROWS = 50;

function emptyRow(): GoalRow {
  return {
    id: makeId("goal"),
    metric: "",
    baseline: null,
    target: null,
    unit: "",
  };
}

/** "" / non-numeric / non-finite → null; otherwise the parsed number. */
function parseNumeric(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/** ((target − baseline) / |baseline|) × 100; null on null/0 baseline or null target. */
function percentChange(
  baseline: number | null,
  target: number | null,
): number | null {
  if (baseline === null || target === null) return null;
  if (!Number.isFinite(baseline) || !Number.isFinite(target)) return null;
  if (baseline === 0) return null;
  return ((target - baseline) / Math.abs(baseline)) * 100;
}

/** "—" | "0%" | "+9.3%" | "−50%" — signed, ≤1 decimal with trailing .0 trimmed. */
function formatPercentChange(pct: number | null): string {
  if (pct === null || !Number.isFinite(pct)) return "—";
  const rounded = Math.round(pct * 10) / 10;
  if (rounded === 0) return "0%";
  const sign = rounded > 0 ? "+" : "−"; // U+2212 minus
  const magnitude = Math.abs(rounded);
  const body = Number.isInteger(magnitude)
    ? String(magnitude)
    : magnitude.toFixed(1);
  return `${sign}${body}%`;
}

export function GoalsWidget({
  data,
  onChange,
  readOnly,
}: WidgetBodyProps<GoalsParams>): ReactElement {
  const { rows } = data;
  const setRows = (next: GoalRow[]): void => {
    onChange({ ...data, rows: next });
  };

  const updateRow = (
    rowId: string,
    patch: Partial<Omit<GoalRow, "id">>,
  ): void =>
    setRows(rows.map((r) => (r.id === rowId ? { ...r, ...patch } : r)));
  // Mirror GoalsParamsSchema's `z.array(GoalRow).max(50)` at the point of entry
  // so a click never produces an onChange payload that fails validation.
  const atRowCap = rows.length >= MAX_GOAL_ROWS;
  const addRow = (): void => {
    if (atRowCap) return;
    setRows([...rows, emptyRow()]);
  };
  const removeRow = (rowId: string): void =>
    setRows(rows.filter((r) => r.id !== rowId));

  const columns: DataTableColumn<GoalRow>[] = [
    {
      id: "metric",
      header: "Metric",
      width: "32%",
      cell: (row) => (
        <TextCell
          value={row.metric}
          ariaLabel={`Metric for ${metricName(row)}`}
          placeholder="Metric"
          readOnly={readOnly}
          onChange={(metric) => updateRow(row.id, { metric })}
        />
      ),
    },
    {
      id: "baseline",
      header: "Baseline",
      align: "end",
      cell: (row) => (
        <NumberCell
          key={`baseline-${readOnly ? "read-only" : "editable"}`}
          value={row.baseline}
          ariaLabel={`Baseline for ${metricName(row)}`}
          readOnly={readOnly}
          onCommit={(baseline) => updateRow(row.id, { baseline })}
        />
      ),
    },
    {
      id: "target",
      header: "Target",
      align: "end",
      cell: (row) => (
        <NumberCell
          key={`target-${readOnly ? "read-only" : "editable"}`}
          value={row.target}
          ariaLabel={`Target for ${metricName(row)}`}
          readOnly={readOnly}
          onCommit={(target) => updateRow(row.id, { target })}
        />
      ),
    },
    {
      id: "unit",
      header: "Unit",
      cell: (row) => (
        <TextCell
          value={row.unit}
          ariaLabel={`Unit for ${metricName(row)}`}
          placeholder="unit"
          readOnly={readOnly}
          onChange={(unit) => updateRow(row.id, { unit })}
        />
      ),
    },
    {
      id: "change",
      header: "Change",
      align: "end",
      cell: (row) =>
        formatPercentChange(percentChange(row.baseline, row.target)),
    },
    ...(readOnly
      ? []
      : [
          {
            id: "actions",
            header: <span className="sr-only">Actions</span>,
            width: 40,
            align: "center" as const,
            cell: (row: GoalRow) => (
              <Button
                variant="ghost"
                tone="critical"
                size="xs"
                iconOnly
                // Shown on row hover or keyboard focus only, as before the
                // DataTable swap; the DataTable row carries no group class, so
                // the row is matched by element.
                className="opacity-0 focus-visible:opacity-100 [tr:hover_&]:opacity-100"
                onClick={() => removeRow(row.id)}
                aria-label={`Remove goal ${metricName(row)}`}
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            ),
          },
        ]),
  ];

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto">
        <DataTable
          caption="Goals"
          layout="fixed"
          density="compact"
          textSize="small"
          columns={columns}
          data={rows}
          getRowId={(row) => row.id}
          emptyState={
            <EmptyState
              size="sm"
              title={
                readOnly
                  ? "No goals yet."
                  : "No goals yet. Click + Add goal below to get started."
              }
            />
          }
        />

        {readOnly ? null : (
          <div className="px-2 py-2">
            <DisabledReason
              {...(atRowCap
                ? { reason: `Limit is ${MAX_GOAL_ROWS} goals` }
                : {})}
            >
              <Button
                variant="dashed"
                size="sm"
                onClick={addRow}
                className="w-full"
              >
                + Add goal
              </Button>
            </DisabledReason>
          </div>
        )}
      </div>
    </div>
  );
}

function metricName(row: GoalRow): string {
  return row.metric || "goal";
}

/** A text cell: the ghost design-system Input, or the value when read-only. */
function TextCell({
  value,
  ariaLabel,
  placeholder,
  readOnly,
  onChange,
}: {
  value: string;
  ariaLabel: string;
  placeholder: string;
  readOnly: boolean | undefined;
  onChange: (text: string) => void;
}): ReactElement {
  if (readOnly) {
    return value ? (
      <>{value}</>
    ) : (
      <span className="italic text-pb-text-muted">—</span>
    );
  }
  return (
    <Input
      variant="ghost"
      size="inline"
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      // Metric + unit are `ShortText` (≤255) in GoalsParamsSchema — cap at the
      // input so an edit can never emit params that fail validation.
      maxLength={255}
    />
  );
}

/** A number cell. It keeps the typed text as a draft and commits on blur, so
 *  an intermediate string like "1." survives typing; a non-numeric entry
 *  commits null. */
function NumberCell({
  value,
  ariaLabel,
  readOnly,
  onCommit,
}: {
  value: number | null;
  ariaLabel: string;
  readOnly: boolean | undefined;
  onCommit: (next: number | null) => void;
}): ReactElement {
  const committed = value === null ? "" : String(value);
  const [raw, setRaw] = useState(committed);
  // Re-sync when the committed value changes underneath us (e.g. a refetch)
  // and no local edit is in flight.
  const [lastSeen, setLastSeen] = useState(committed);
  if (committed !== lastSeen) {
    setLastSeen(committed);
    setRaw(committed);
  }

  if (readOnly) {
    return value === null ? (
      <span className="italic text-pb-text-muted">—</span>
    ) : (
      <>{String(value)}</>
    );
  }
  return (
    <Input
      variant="ghost"
      size="inline"
      aria-label={ariaLabel}
      inputMode="decimal"
      value={raw}
      onChange={(e) => setRaw(e.target.value)}
      onBlur={() => onCommit(parseNumeric(raw))}
      placeholder="—"
      className="text-end"
    />
  );
}
